import { type ComponentProps, type ReactNode } from 'react';
import { Link } from '@/shared-ui/atoms/link';
import { Button } from '@/shared-ui/atoms/button';
import { Icon } from '@/shared-ui/atoms/icon';
import { InsideSpaceIcon } from '@/shared-ui/icons/inside-space';
import { cn } from '@/lib/utils';
import { parseInsideSpaceLogoSizePx, parseInsideSpacePaddingPx } from './inside-space-config';
import type { InsideSpaceCardsLayout } from './inside-space-layout';

const HEX_COLOR_REGEX = /^#(?:[0-9A-Fa-f]{3}){1,2}$/;

function isValidHexBackground(value: string | undefined): value is string {
    const trimmed = value?.trim();
    return Boolean(trimmed && HEX_COLOR_REGEX.test(trimmed));
}

export type InsideSpaceLogoSize = 'compact' | 'default' | 'large';

const LOGO_WIDTH_CLASS: Record<InsideSpaceLogoSize, string> = {
    compact: 'w-[120px]',
    default: 'w-[164px] md:w-[204px]',
    large: 'w-[204px] md:w-[240px]',
};

const LOGO_ICON_HEIGHT_CLASS: Record<InsideSpaceLogoSize, string> = {
    compact: 'h-10',
    default: 'h-12 sm:h-14',
    large: 'h-14 sm:h-16',
};

export interface InsideSpaceProps extends ComponentProps<'section'> {
    /** When true, renders the default Inside Space wordmark in the section header. */
    showDefaultLogo?: boolean;
    /** Width preset for the default Inside Space wordmark (used when `logoSizePx` is not set). */
    logoSize?: InsideSpaceLogoSize;
    /** Logo width in pixels (takes precedence over `logoSize` preset). */
    logoSizePx?: number;
    /** Uniform section padding in pixels (same at all breakpoints). */
    paddingPx?: number;
    /** Layout for nested link cards. */
    cardsLayout?: InsideSpaceCardsLayout;
    /** CTA label (shown with `ctaTo` when both are set). */
    ctaLabel?: string;
    /** CTA destination (site-relative path recommended). */
    ctaTo?: string;
    /** Section background as a hex colour (e.g. `#f6f2ed`). Invalid values fall back to the default cream token. */
    backgroundColor?: string;
    /** Main card grid (e.g. link cards). */
    cards?: ReactNode;
}

function InsideSpaceDefaultLogo({
    className,
    logoSize = 'default',
    logoSizePx,
}: {
    className?: string;
    logoSize?: InsideSpaceLogoSize;
    logoSizePx?: number;
}) {
    const resolvedLogoSizePx = parseInsideSpaceLogoSizePx(logoSizePx);
    const usePresetSizing = resolvedLogoSizePx === undefined;

    return (
        <div
            role="img"
            aria-label="Inside Space"
            className={cn(
                'flex items-start gap-2 text-foreground',
                usePresetSizing ? LOGO_WIDTH_CLASS[logoSize] : undefined,
                className
            )}
            style={usePresetSizing ? undefined : { width: resolvedLogoSizePx }}>
            <Icon
                icon={InsideSpaceIcon}
                className={cn('w-full shrink-0', usePresetSizing ? LOGO_ICON_HEIGHT_CLASS[logoSize] : 'h-auto')}
                aria-hidden
            />
        </div>
    );
}

export const InsideSpace = ({
    className,
    showDefaultLogo = true,
    logoSize = 'default',
    logoSizePx,
    paddingPx,
    ctaLabel,
    ctaTo,
    backgroundColor,
    cards,
    ...props
}: InsideSpaceProps) => {
    const hasCta = Boolean(ctaLabel?.trim() && ctaTo?.trim());
    const safeBackgroundHex = isValidHexBackground(backgroundColor) ? backgroundColor.trim() : undefined;
    const resolvedPaddingPx = parseInsideSpacePaddingPx(paddingPx);
    const useDefaultPadding = resolvedPaddingPx === undefined;

    return (
        <section
            className={cn(
                useDefaultPadding && 'px-4 py-6 text-foreground md:px-8 md:py-10',
                !useDefaultPadding && 'text-foreground',
                !safeBackgroundHex && 'bg-snk-cream',
                className
            )}
            style={{
                ...(safeBackgroundHex ? { backgroundColor: safeBackgroundHex } : {}),
                ...(resolvedPaddingPx !== undefined ? { padding: resolvedPaddingPx } : {}),
            }}
            {...props}>
            {showDefaultLogo ? (
                <header className="mb-6">
                    <InsideSpaceDefaultLogo logoSize={logoSize} logoSizePx={logoSizePx} />
                </header>
            ) : null}

            {cards}

            {hasCta && ctaTo && ctaLabel ? (
                <Button asChild className="m-0 mt-6 tracking-normal" variant="tertiary" size="sm">
                    <Link to={ctaTo}>{ctaLabel}</Link>
                </Button>
            ) : null}
        </section>
    );
};
